import { RUSSIA_REQUEST_END_ADDRESS_PLACEHOLDER } from './russia-requests.js';

/**
 * @aiPurpose SEND-readiness predicate for the required «Адрес доставки»
 *   (`endAddress`) on a Russia request (#1579). A row is missing its
 *   delivery address iff `endAddress` is null, empty / whitespace-only
 *   after trim, OR still equals the `from-parent` placeholder sentinel
 *   (`RUSSIA_REQUEST_END_ADDRESS_PLACEHOLDER`) — the snapshot default the
 *   manager has not yet replaced. Applies to BOTH SIMPLE and COMPLEX.
 * @aiInvariant returns `['endAddress']` when missing, else `[]`. The
 *   `'endAddress'` key is the only thing the wire surfaces in
 *   `meta.missing`; the raw address value NEVER crosses the gate.
 */
export function russiaEndAddressSendMissing(endAddress: string | null): string[] {
  const trimmed = endAddress?.trim() ?? '';
  if (trimmed.length === 0 || trimmed === RUSSIA_REQUEST_END_ADDRESS_PLACEHOLDER) {
    return ['endAddress'];
  }
  return [];
}

/**
 * @aiPurpose single-source SEND-readiness predicate for a Russia
 *   «Простая» (SIMPLE) request (#1545). Mirror of the China-side
 *   `computeDraftIncompleteMissing` SIMPLE branch, trimmed to the flat
 *   Russia position shape (no boxes, no dimensions/packages). A SIMPLE
 *   request is send-ready iff it carries at least ONE valid position.
 *
 *   A position is "valid" iff:
 *     - `name.trim()` is non-empty, AND
 *     - `description.trim()` is non-empty, AND
 *     - `weightKg` is non-null and strictly positive, AND
 *     - `volumeM3` is non-null and strictly positive.
 *
 *   The trim-aware string check is deliberate: a single space does not
 *   satisfy "non-empty" at SEND time even though DRAFT save can persist
 *   it. The photo cap (`> REQUEST_ITEM_PHOTOS_MAX`) is enforced by the
 *   schema `.max(5)` in ALL states, so it is NOT re-checked here.
 *
 * @aiInvariant returns `[]` when >= 1 valid position exists, else
 *   `['items']`. The `'items'` key is the single stable missing-field
 *   identifier the wire surfaces in `meta.missing` so the client maps it
 *   to one localized toast.
 */
export function russiaSimpleSendMissing(
  items: ReadonlyArray<{
    name: string;
    description: string;
    weightKg: number | null;
    volumeM3: number | null;
  }>,
): string[] {
  const hasValid = items.some(
    (it) =>
      it.name.trim().length > 0 &&
      it.description.trim().length > 0 &&
      it.weightKg !== null &&
      it.weightKg > 0 &&
      it.volumeM3 !== null &&
      it.volumeM3 > 0,
  );
  return hasValid ? [] : ['items'];
}

/**
 * @aiPurpose SEND-readiness predicate for a Russia «Сложная» (COMPLEX)
 *   request (#1546, FR-5). A COMPLEX request carries request-level totals
 *   instead of per-position weight/volume, so it is send-ready iff BOTH
 *   `totalWeight` AND `totalVolume` are present and strictly positive —
 *   the same strictly-positive numeric rule the SIMPLE predicate applies
 *   to per-position weight/volume.
 *
 * @aiInvariant returns the subset of `['totalWeight', 'totalVolume']`
 *   that is missing (null or <= 0), in that stable order; `[]` when both
 *   are present and positive. The keys mirror the wire field names so the
 *   client maps each to its localized field error.
 */
export function russiaComplexSendMissing(totals: {
  totalWeight: number | null;
  totalVolume: number | null;
}): string[] {
  const missing: string[] = [];
  if (totals.totalWeight === null || totals.totalWeight <= 0) missing.push('totalWeight');
  if (totals.totalVolume === null || totals.totalVolume <= 0) missing.push('totalVolume');
  return missing;
}

/**
 * @aiPurpose kind-vs-payload mutual-exclusivity predicate (#1546, FR-6).
 *   A SIMPLE request lives on positions and MUST carry no request-level
 *   totals; a COMPLEX request lives on request-level totals and MUST
 *   carry no positions. This invariant holds in EVERY state — including
 *   DRAFT, which is otherwise exempt from the presence rules — so a row
 *   can never carry both payloads at once.
 *
 * @aiInvariant returns the offending keys: `['totalWeight'/'totalVolume']`
 *   when a SIMPLE row carries either total, or `['items']` when a COMPLEX
 *   row carries any position. `[]` when the payload matches the kind.
 */
export function russiaKindExclusivityMissing(input: {
  kind: 'SIMPLE' | 'COMPLEX';
  itemCount: number;
  totalWeight: number | null;
  totalVolume: number | null;
}): string[] {
  const offending: string[] = [];
  if (input.kind === 'SIMPLE') {
    if (input.totalWeight !== null) offending.push('totalWeight');
    if (input.totalVolume !== null) offending.push('totalVolume');
  } else if (input.itemCount > 0) {
    offending.push('items');
  }
  return offending;
}

/**
 * @aiPurpose single-source SEND-readiness predicate for a Russia request
 *   of either kind (#1546). Composes the kind-specific presence predicate
 *   (`russiaSimpleSendMissing` / `russiaComplexSendMissing`) with the
 *   always-on mutual-exclusivity rule (`russiaKindExclusivityMissing`).
 *   The backend SEND gate (a LATER stage) calls this; DRAFT save does NOT
 *   apply the presence rules (only exclusivity) and is out of scope here.
 *
 * @aiInvariant returns the de-duplicated union of every missing /
 *   offending key. `[]` means the request is send-ready. The required
 *   `endAddress` (#1579) is checked first so it surfaces ahead of the
 *   presence (kind-specific) keys and any exclusivity violation.
 */
/**
 * @aiPurpose bidding-completeness predicate for the IN_WORK -> PROCESSED
 *   (`process`) transition when a Russia request quotes per-departure-city
 *   rates (#1580). The bidding stage is complete only when EVERY
 *   `RussiaRequestStartCity` has at least one START_CITY-scope bid. Returns
 *   the start-city ids (arrivalCityId UUIDs) that still lack a rate, in the
 *   input order, deduplicated; `[]` when every city is covered.
 *
 *   The gate is a no-op for legacy / non-per-city requests: pass an empty
 *   `startCityIds` (the request has no start cities) OR a `coveredCityIds`
 *   set that already covers them. A request that never used START_CITY bids
 *   reaches `process` through the existing answered-carrier gate unchanged.
 *
 * @aiInvariant the returned values are `ArrivalCity` UUIDs — reference-data
 *   ids / keys only, NEVER customer PII. Callers surface them as the stable
 *   `missing[]` contract; the wire never carries the customer-facing city
 *   copy through this gate.
 */
export function russiaProcessMissingStartCities(input: {
  startCityIds: ReadonlyArray<string>;
  coveredCityIds: ReadonlySet<string>;
}): string[] {
  const missing: string[] = [];
  for (const cityId of input.startCityIds) {
    if (!input.coveredCityIds.has(cityId) && !missing.includes(cityId)) {
      missing.push(cityId);
    }
  }
  return missing;
}

export function russiaSendMissing(input: {
  kind: 'SIMPLE' | 'COMPLEX';
  items: ReadonlyArray<{
    name: string;
    description: string;
    weightKg: number | null;
    volumeM3: number | null;
  }>;
  totalWeight: number | null;
  totalVolume: number | null;
  endAddress: string | null;
}): string[] {
  const endAddress = russiaEndAddressSendMissing(input.endAddress);
  const presence =
    input.kind === 'SIMPLE'
      ? russiaSimpleSendMissing(input.items)
      : russiaComplexSendMissing(input);
  const exclusivity = russiaKindExclusivityMissing({
    kind: input.kind,
    itemCount: input.items.length,
    totalWeight: input.totalWeight,
    totalVolume: input.totalVolume,
  });
  return [...new Set([...endAddress, ...presence, ...exclusivity])];
}
